import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database, Json } from '../generated';
import type { IntegrationKeySourceLabel } from './integrationKeys';
import {
  buildNextCursor,
  clampPageSize,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---------- client (the row itself) ---------------------------------------

/**
 * Inbound-integration provenance columns added by
 * `supabase/migrations/20260606000200_clients_orders_payments_source_columns.sql`.
 *
 * Generated Database types lag the migration (codegen runs at deploy);
 * we intersect them onto the Row type here so detail pages can render
 * the "imported from <source>" chip without an `as any` cast.
 */
export interface ClientSourceColumns {
  source_integration_key_id: string | null;
  source_external_id: string | null;
  source_label: IntegrationKeySourceLabel | null;
}

export type Client = Database['clients']['Tables']['clients']['Row'] & ClientSourceColumns;
// Insert / Update intentionally do NOT carry the ClientSourceColumns
// intersection: operator-driven writes never set the source_* fields
// (only the integration-api edge function does), and the supabase-js
// builders use the generated `RejectExcessProperties` mapped type to
// forbid unknown columns at the call site.
export type ClientInsert = Database['clients']['Tables']['clients']['Insert'];
export type ClientUpdate = Database['clients']['Tables']['clients']['Update'];
export type ClientSummary = Database['clients']['Views']['v_client_summary']['Row'];
export type ClientStatus = Database['clients']['Enums']['client_status'];
export type ClientType = Database['clients']['Enums']['client_type'];

export interface ListClientsInput extends KeysetInput {
  search?: string;
  status?: ClientStatus;
  clientType?: ClientType;
  tag?: string;
  /**
   * Visibility scope for client lookup.
   * - 'store' (default): active store + cross-store grants for that store.
   * - 'org': every client in the org regardless of store.
   */
  scope?: 'store' | 'org';
  /**
   * Whether archived clients are included in the result. Defaults to
   * false so the partial keyset index `clients_clients_list_keyset_idx`
   * (where status <> 'archived') is hit on the hot path.
   */
  includeArchived?: boolean;
}

/**
 * Paginated, keyset-cursor-driven list of clients reachable from the
 * active store.
 *
 * Why this is an RPC and not a direct PostgREST `.from('clients')` call:
 * the 2026-06-23 org-level refactor (`clients.client_store_access`)
 * made client-to-store membership M:N. A single client can have a
 * legacy primary `store_id` AND/OR one or more rows in
 * `client_store_access` that grant additional stores access. The v1
 * filter `.eq('store_id', storeId)` silently dropped every cross-store
 * granted client and every org-level client (`store_id IS NULL`),
 * which manifested as empty client pickers in OrderNewPage,
 * AppointmentNewPage, and elsewhere after the OfficeHub ETL backfill.
 * `clients.rpc_list_visible_clients` consolidates the two paths in one
 * `security definer` function bound to the same partial keyset index.
 *
 * Service-level contract:
 *   * Returns clients reachable from the active store via either path.
 *   * Stable `nextCursor` on `(updated_at desc, id desc)`; null = EOL.
 *   * `total` is populated from `clients.rpc_count_visible_clients`
 *     when `countMode: 'exact'` (cheaper than 'estimated' here because
 *     the RPC predicate is index-aligned). `'estimated'` falls back to
 *     `'exact'` for now -- planner estimates through a security-definer
 *     RPC are unreliable. `'none'` (default) skips the count call.
 */
export async function listClients(
  ctx: ActiveContext,
  input: ListClientsInput = {},
): Promise<ListResult<Client>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);
  const scopedStoreId = input.scope === 'org' ? null : storeId;

  // The generated Database['clients']['Functions'] union lags this
  // migration; the `as never` casts pin the rpc name + arg shape so we
  // don't have to wait for codegen. Runtime types match the SQL exactly.
  const { data, error } = await supabase()
    .schema('clients')
    .rpc('rpc_list_visible_clients' as never, {
      p_org_id: orgId,
      p_store_id: scopedStoreId,
      p_search: input.search ?? null,
      p_status: input.status ?? null,
      p_client_type: input.clientType ?? null,
      p_tag: input.tag ?? null,
      p_include_archived: input.includeArchived ?? false,
      p_cursor: (input.cursor as unknown as Json) ?? null,
      p_page_size: pageSize,
    } as never);
  if (error) throw error;
  const rows = (data ?? []) as unknown as Client[];

  let total: number | null = null;
  if (input.countMode === 'exact' || input.countMode === 'estimated') {
    const { data: countData, error: countError } = await supabase()
      .schema('clients')
      .rpc('rpc_count_visible_clients' as never, {
        p_org_id: orgId,
        p_store_id: scopedStoreId,
        p_search: input.search ?? null,
        p_status: input.status ?? null,
        p_client_type: input.clientType ?? null,
        p_tag: input.tag ?? null,
        p_include_archived: input.includeArchived ?? false,
      } as never);
    if (countError) throw countError;
    if (typeof countData === 'number') total = countData;
    else if (typeof countData === 'string') total = Number(countData) || 0;
  }

  return {
    rows,
    nextCursor: buildNextCursor(rows as unknown as Array<Record<string, unknown>>, pageSize, 'updated_at'),
    total,
    totalIsExact: input.countMode === 'exact' || input.countMode === 'estimated',
  };
}

/**
 * Convenience wrapper for one-shot small fetches (dashboard widgets,
 * search-as-you-type pickers). Returns the bare `Client[]` so call sites
 * that don't care about pagination stay simple.
 *
 * Defaults to a page size of 25 -- well under the PostgREST `max_rows`
 * cap (1000) and well under the keyset list service's default of 50.
 */
export async function listClientsTop(
  ctx: ActiveContext,
  input: Omit<ListClientsInput, 'cursor' | 'countMode'> & { limit?: number } = {},
): Promise<Client[]> {
  const { rows } = await listClients(ctx, {
    ...input,
    pageSize: input.limit ?? 25,
    countMode: 'none',
  });
  return rows;
}

export async function getClient(ctx: ActiveContext, id: string): Promise<Client | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('clients')
    .from('clients')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  // Generated Row type lags the source_* columns added by the
  // 20260606000200 migration -- the runtime row includes them, so the
  // cast onto our extended `Client` type is sound.
  return (data ?? null) as Client | null;
}

export interface CreateClientInput {
  /** Either set displayName explicitly OR provide first/last and we'll build it. */
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  dateOfBirth?: string | null;
  clientType?: ClientType;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  status?: ClientStatus;
  tags?: string[];
  metadata?: Json;
  communicationPreferences?: Json;
  referredById?: string | null;
}

/**
 * Builds a sensible display_name when the caller didn't provide one explicitly.
 * Company name wins if set, otherwise first+last, otherwise email, otherwise '?'.
 */
function deriveDisplayName(input: CreateClientInput | UpdateClientInput): string | undefined {
  if (input.displayName && input.displayName.trim().length > 0) return input.displayName.trim();
  if (input.companyName && input.companyName.trim().length > 0) return input.companyName.trim();
  const fn = (input.firstName ?? '').trim();
  const ln = (input.lastName ?? '').trim();
  const both = [fn, ln].filter(Boolean).join(' ');
  if (both.length > 0) return both;
  if (input.email && input.email.trim().length > 0) return input.email.trim();
  return undefined;
}

export async function createClient(
  ctx: ActiveContext,
  input: CreateClientInput,
): Promise<Client> {
  const { orgId, storeId } = requireContext(ctx);
  const dn = deriveDisplayName(input);
  if (!dn) throw new Error('A client needs at least a name, company, or email.');
  const insert: ClientInsert = {
    org_id: orgId,
    store_id: storeId,
    display_name: dn,
    first_name: input.firstName ?? undefined,
    last_name: input.lastName ?? undefined,
    company_name: input.companyName ?? undefined,
    date_of_birth: input.dateOfBirth ?? undefined,
    client_type: input.clientType ?? 'retail',
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    notes: input.notes ?? undefined,
    status: input.status ?? 'active',
    tags: input.tags ?? [],
    metadata: input.metadata ?? {},
    communication_preferences: input.communicationPreferences ?? {},
    referred_by_id: input.referredById ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('clients')
    .from('clients')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data as Client;
}

export interface UpdateClientInput {
  id: string;
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  dateOfBirth?: string | null;
  clientType?: ClientType;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  status?: ClientStatus;
  tags?: string[];
  metadata?: Json;
  communicationPreferences?: Json;
  referredById?: string | null;
}

export async function updateClient(
  ctx: ActiveContext,
  input: UpdateClientInput,
): Promise<Client> {
  requireContext(ctx);
  const patch: ClientUpdate = {};
  // Always recompute display_name if any name-related field is touched and the
  // caller didn't pass an explicit displayName.
  const touchedName =
    input.displayName !== undefined ||
    input.firstName !== undefined ||
    input.lastName !== undefined ||
    input.companyName !== undefined;
  if (touchedName) {
    const dn = deriveDisplayName(input);
    if (dn !== undefined) patch.display_name = dn;
  }
  if (input.firstName !== undefined) patch.first_name = input.firstName;
  if (input.lastName !== undefined) patch.last_name = input.lastName;
  if (input.companyName !== undefined) patch.company_name = input.companyName;
  if (input.dateOfBirth !== undefined) patch.date_of_birth = input.dateOfBirth;
  if (input.clientType !== undefined) patch.client_type = input.clientType;
  if (input.email !== undefined) patch.email = input.email;
  if (input.phone !== undefined) patch.phone = input.phone;
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.status !== undefined) patch.status = input.status;
  if (input.tags !== undefined) patch.tags = input.tags;
  if (input.metadata !== undefined) patch.metadata = input.metadata;
  if (input.communicationPreferences !== undefined)
    patch.communication_preferences = input.communicationPreferences;
  if (input.referredById !== undefined) patch.referred_by_id = input.referredById;

  const { data, error } = await supabase()
    .schema('clients')
    .from('clients')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Client;
}

export async function archiveClient(ctx: ActiveContext, id: string): Promise<void> {
  await updateClient(ctx, { id, status: 'archived' });
}

// ---------- bulk import --------------------------------------------------

/**
 * Single row payload accepted by `bulkImportClients`. Mirrors the shape
 * accepted by `clients.rpc_bulk_import_clients(p_rows jsonb)` in
 * `supabase/migrations/20260522001100_clients_bulk_import_rpc.sql`.
 *
 * Field semantics match `CreateClientInput` -- the migration's
 * `display_name` derivation matches `deriveDisplayName(...)` above so
 * the bulk path produces rows indistinguishable from per-row inserts.
 */
export interface BulkImportClientRow {
  display_name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  notes?: string;
  status?: ClientStatus;
  client_type?: ClientType;
  date_of_birth?: string;
  tags?: string[];
  metadata?: Json;
  communication_preferences?: Json;
}

/** Per-row import outcome returned by the RPC. */
export interface BulkImportClientResult {
  row_number: number;
  id: string | null;
  ok: boolean;
  error: string | null;
}

/**
 * Cap per call. The migration enforces this server-side (errcode 22023);
 * we mirror it here so the operator UI can chunk a 50,000-row CSV into
 * batches without a round-trip to discover the cap.
 */
export const BULK_IMPORT_CLIENTS_BATCH_CAP = 5000;

/**
 * Bulk-insert up to 5,000 clients in a single round trip. Returns a
 * per-row outcome array so the caller can render an import report
 * (success count, error count, per-row reasons).
 *
 * Larger imports: the caller should chunk into 5,000-row batches and
 * call this function once per batch. A 50,000-client onboarding then
 * takes 10 round trips instead of 50,000.
 */
export async function bulkImportClients(
  ctx: ActiveContext,
  rows: BulkImportClientRow[],
): Promise<BulkImportClientResult[]> {
  const { orgId, storeId } = requireContext(ctx);
  if (rows.length === 0) return [];
  if (rows.length > BULK_IMPORT_CLIENTS_BATCH_CAP) {
    throw new Error(
      `bulkImportClients: ${rows.length} rows exceeds per-call cap of ${BULK_IMPORT_CLIENTS_BATCH_CAP}.` +
        ' Chunk the input on the caller side.',
    );
  }
  const { data, error } = await supabase()
    .schema('clients')
    .rpc('rpc_bulk_import_clients', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_rows: rows as unknown as Json,
    });
  if (error) throw error;
  return (data ?? []) as BulkImportClientResult[];
}

export async function getClientSummary(
  ctx: ActiveContext,
  id: string,
): Promise<ClientSummary | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('clients')
    .from('v_client_summary')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// ---------- addresses -----------------------------------------------------

export type ClientAddress = Database['clients']['Tables']['client_addresses']['Row'];
export type ClientAddressInsert = Database['clients']['Tables']['client_addresses']['Insert'];

export async function listClientAddresses(
  ctx: ActiveContext,
  clientId: string,
): Promise<ClientAddress[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_addresses')
    .select('*')
    .eq('client_id', clientId)
    .order('is_default_shipping', { ascending: false })
    .order('is_default_billing', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertClientAddressInput {
  id?: string;
  clientId: string;
  label?: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  isDefaultBilling?: boolean;
  isDefaultShipping?: boolean;
  notes?: string | null;
}

export async function createClientAddress(
  ctx: ActiveContext,
  input: UpsertClientAddressInput,
): Promise<ClientAddress> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.isDefaultBilling) await clearDefault(ctx, input.clientId, 'billing');
  if (input.isDefaultShipping) await clearDefault(ctx, input.clientId, 'shipping');

  const row: ClientAddressInsert = {
    org_id: orgId,
    store_id: storeId,
    client_id: input.clientId,
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_billing: Boolean(input.isDefaultBilling),
    is_default_shipping: Boolean(input.isDefaultShipping),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_addresses')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as ClientAddress;
}

export async function updateClientAddress(
  ctx: ActiveContext,
  input: UpsertClientAddressInput & { id: string },
): Promise<ClientAddress> {
  requireContext(ctx);
  if (input.isDefaultBilling) await clearDefault(ctx, input.clientId, 'billing', input.id);
  if (input.isDefaultShipping) await clearDefault(ctx, input.clientId, 'shipping', input.id);

  const patch: Partial<ClientAddressInsert> = {
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_billing: Boolean(input.isDefaultBilling),
    is_default_shipping: Boolean(input.isDefaultShipping),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_addresses')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ClientAddress;
}

export async function deleteClientAddress(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('clients')
    .from('client_addresses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/**
 * Clears the existing default-billing / default-shipping flag before setting a
 * new one. The unique partial indexes on the table would otherwise reject the
 * insert/update.
 */
async function clearDefault(
  ctx: ActiveContext,
  clientId: string,
  kind: 'billing' | 'shipping',
  exceptId?: string,
) {
  requireContext(ctx);
  const patch =
    kind === 'billing' ? { is_default_billing: false } : { is_default_shipping: false };
  const col = kind === 'billing' ? 'is_default_billing' : 'is_default_shipping';
  let q = supabase()
    .schema('clients')
    .from('client_addresses')
    .update(patch)
    .eq('client_id', clientId)
    .eq(col, true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

// ---------- notes ---------------------------------------------------------

export type ClientNote = Database['clients']['Tables']['client_notes']['Row'];
export type ClientNoteInsert = Database['clients']['Tables']['client_notes']['Insert'];

export async function listClientNotes(
  ctx: ActiveContext,
  clientId: string,
): Promise<ClientNote[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_notes')
    .select('*')
    .eq('client_id', clientId)
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

export interface CreateClientNoteInput {
  clientId: string;
  body: string;
  isPinned?: boolean;
  visibility?: 'store' | 'org' | 'private';
  parentNoteId?: string | null;
}

export async function createClientNote(
  ctx: ActiveContext,
  input: CreateClientNoteInput,
): Promise<ClientNote> {
  const { orgId, storeId } = requireContext(ctx);
  const { data: userData } = await supabase().auth.getUser();
  const authorId = userData.user?.id ?? null;
  const row: ClientNoteInsert = {
    org_id: orgId,
    store_id: storeId,
    client_id: input.clientId,
    author_id: authorId,
    body: input.body,
    is_pinned: Boolean(input.isPinned),
    visibility: input.visibility ?? 'store',
    parent_note_id: input.parentNoteId ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_notes')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as ClientNote;
}

export async function togglePinClientNote(
  ctx: ActiveContext,
  id: string,
  isPinned: boolean,
): Promise<ClientNote> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('clients')
    .from('client_notes')
    .update({ is_pinned: isPinned })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ClientNote;
}

export async function deleteClientNote(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('clients')
    .from('client_notes')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export function clientDisplayName(
  c: Pick<Client, 'company_name' | 'first_name' | 'last_name' | 'display_name'>,
): string {
  return (
    c.company_name?.trim() ||
    [c.first_name, c.last_name].filter(Boolean).join(' ').trim() ||
    c.display_name
  );
}

export async function getClientDisplayNamesByIds(
  ctx: ActiveContext,
  ids: string[],
): Promise<Map<string, string>> {
  const { orgId } = requireContext(ctx);
  const unique = [...new Set(ids.filter(Boolean))];
  const out = new Map<string, string>();
  if (unique.length === 0) return out;

  const { data, error } = await supabase()
    .schema('clients')
    .from('clients')
    .select('id, display_name, first_name, last_name, company_name')
    .eq('org_id', orgId)
    .in('id', unique);
  if (error) throw error;

  for (const row of (data ?? []) as Client[]) {
    out.set(row.id, clientDisplayName(row));
  }
  return out;
}

export async function lookupClientsByPhoneDigits(
  ctx: ActiveContext,
  storeId: string,
  phoneDigits: string[],
): Promise<Map<string, { clientId: string; displayName: string }>> {
  const { orgId } = requireContext(ctx);
  const unique = [...new Set(phoneDigits.filter(Boolean))];
  const out = new Map<string, { clientId: string; displayName: string }>();
  if (unique.length === 0) return out;

  const { data, error } = await supabase()
    .schema('clients')
    .rpc('rpc_lookup_clients_by_phone_digits', {
      p_org_id: orgId,
      p_store_id: storeId,
      p_phone_digits: unique,
    });
  if (error) throw error;

  for (const row of data ?? []) {
    out.set(row.phone_digits, {
      clientId: row.client_id,
      displayName: row.display_name,
    });
  }
  return out;
}
